import { FC, useRef, useState } from 'react'
import { SentimentDonut as SentimentDonutChart } from 'src/components/SentimentDonut'
import { ReactComponent as ArrowRightIcon } from 'src/assets/svg/arrow-right-line.svg'
import { ReactComponent as LinkIcon } from 'src/assets/svg/link.svg'
import { ReactComponent as EditIcon } from 'src/pages/AIListening/Main/CampaignCard/svgs/edit-2.svg'
import { ReactComponent as TrashIcon } from 'src/pages/AIListening/Main/CampaignCard/svgs/trash.svg'
import { Tooltip } from 'src/components/Tooltip'
import useOutsideClick from 'src/hooks/useOutsideClick'
import styles from './platformAnalysisCard.module.scss'

type RatingLabel = 'Excellent' | 'Good' | 'Poor'

export interface SentimentData {
  negative: number
  neutral: number
  positive: number
  total: number
}

type CardStatus = 'collecting' | 'failed' | 'pending' | 'ready'

export interface PlatformAnalysisCardProps {
  createdOn: string
  lastUpdate: string | null
  links?: string[]
  onChangeName?: () => void
  onDeleteLocation?: () => void
  onViewDetails?: () => void
  providerIcon: FC<React.SVGProps<SVGSVGElement>>
  providerName: string
  rating: number | null
  reviewCount: number | null
  sentiment: SentimentData | null
  status?: CardStatus
  title: string
}

const PENDING_LABEL = {
  failed: 'Data collection failed',
}

// Friendly fallback label for a source link when no title is set — the bare
// hostname (e.g. "booking.com") rather than the full URL.
const hostLabel = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

export const getRatingLabel = (rating: number): RatingLabel => {
  if (rating >= 4.5) return 'Excellent'
  if (rating >= 3.5) return 'Good'
  return 'Poor'
}

export const renderStars = (rating: number) => {
  return Array.from({ length: 5 }, (_, i) => {
    const filled = i < Math.floor(rating)
    return (
      <span
        className={styles.star}
        key={i}
        style={{ color: filled ? '#f59e0b' : '#d1d5db' }}
      >
        ★
      </span>
    )
  })
}

export const badgeClass = (label: RatingLabel) => {
  if (label === 'Excellent') return styles.badgeExcellent
  if (label === 'Good') return styles.badgeGood
  return styles.badgePoor
}

/** The sentiment ring, with the counts behind it on hover. */
export const SentimentDonut: FC<{ data: SentimentData }> = ({ data }) => {
  const total = data.total || 1
  const share = (value: number) => ((value / total) * 100).toFixed(0)

  const tooltipContent = (
    <div className={styles.donutTooltip}>
      <div className={styles.tooltipRow}>
        <span className={styles.dotPositive} />
        <span className={styles.tooltipLabel}>
          {share(data.positive)}% Positive
        </span>
        <span className={styles.tooltipValue}>
          {data.positive.toLocaleString()}
        </span>
      </div>
      <div className={styles.tooltipRow}>
        <span className={styles.dotNeutral} />
        <span className={styles.tooltipLabel}>
          {share(data.neutral)}% Neutral
        </span>
        <span className={styles.tooltipValue}>
          {data.neutral.toLocaleString()}
        </span>
      </div>
      <div className={styles.tooltipRow}>
        <span className={styles.dotNegative} />
        <span className={styles.tooltipLabel}>
          {share(data.negative)}% Negative
        </span>
        <span className={styles.tooltipValue}>
          {data.negative.toLocaleString()}
        </span>
      </div>
    </div>
  )

  return (
    <Tooltip
      overlay={tooltipContent}
      overlayInnerClassName={styles.tooltipReset}
      placement="top"
    >
      <div className={styles.donutWrapper}>
        <SentimentDonutChart
          bands={[
            { color: '#7c7cff', value: data.positive },
            { color: '#d1d5db', value: data.neutral },
            { color: '#f97066', value: data.negative },
          ]}
        />
        <div className={styles.donutLabel}>
          <span className={styles.donutTitle}>Sentiment</span>
          <span className={styles.donutValue}>
            {data.total.toLocaleString()}
          </span>
        </div>
      </div>
    </Tooltip>
  )
}

export const PlatformAnalysisCard: FC<PlatformAnalysisCardProps> = ({
  createdOn,
  lastUpdate,
  links = [],
  onChangeName,
  onDeleteLocation,
  onViewDetails,
  providerIcon: ProviderIcon,
  providerName,
  rating,
  reviewCount,
  sentiment,
  status = 'ready',
  title,
}) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  useOutsideClick({ onOutsideClick: () => setMenuOpen(false), ref: menuRef })
  const isPending = status !== 'ready' || rating === null || sentiment === null
  const label = getRatingLabel(rating ?? 0)

  return (
    <div className={styles.card}>
      {/* ─── Header ─── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h3 className={styles.title}>{title}</h3>
          <span className={styles.createdOn}>Created on {createdOn}</span>
        </div>
        <div className={styles.headerRight}>
          {lastUpdate && (
            <span className={styles.lastUpdate}>Last update {lastUpdate}</span>
          )}
          <div className={styles.menuWrapper} ref={menuRef}>
            <button
              className={styles.menuBtn}
              onClick={() => setMenuOpen((prev) => !prev)}
              type="button"
            >
              <span className={styles.menuDot} />
              <span className={styles.menuDot} />
              <span className={styles.menuDot} />
            </button>
            {menuOpen && (
              <div className={styles.menuDropdown}>
                <button
                  className={styles.menuItem}
                  onClick={() => {
                    setMenuOpen(false)
                    onChangeName?.()
                  }}
                  type="button"
                >
                  <EditIcon className={styles.menuItemIcon} />
                  Edit
                </button>
                {onDeleteLocation && (
                  <button
                    className={styles.menuItem}
                    onClick={() => {
                      setMenuOpen(false)
                      onDeleteLocation()
                    }}
                    type="button"
                  >
                    <TrashIcon className={styles.menuItemIcon} />
                    Delete location
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Body ─── */}
      <div className={styles.body}>
        <div className={styles.bodyLeft}>
          {/* Provider info */}
          <div className={styles.providerRow}>
            <ProviderIcon className={styles.providerIcon} />
            <span className={styles.providerName}>{providerName}</span>
          </div>

          {isPending ? (
            // No data yet: mirror the normal layout with zeroed values (0.0,
            // empty stars, 0 reviews — no quality badge, it would be a lie).
            // Only a failed fetch shows a message instead.
            status === 'failed' ? (
              <div className={`${styles.pending} ${styles.pendingFailed}`}>
                <span>{PENDING_LABEL.failed}</span>
              </div>
            ) : (
              <>
                <div className={styles.ratingRow}>
                  <span className={styles.ratingValue}>0.0</span>
                  <div className={styles.stars}>{renderStars(0)}</div>
                </div>
                <p className={styles.reviewCount}>Based on 0 Reviews</p>
              </>
            )
          ) : (
            <>
              {/* Rating row */}
              <div className={styles.ratingRow}>
                <span className={styles.ratingValue}>
                  {(rating as number).toFixed(1)}
                </span>
                <div className={styles.stars}>
                  {renderStars(rating as number)}
                </div>
                <span className={`${styles.badge} ${badgeClass(label)}`}>
                  {label}
                </span>
              </div>

              {/* Review count */}
              <p className={styles.reviewCount}>
                Based on {(reviewCount ?? 0).toLocaleString()} Reviews
              </p>
            </>
          )}
        </div>

        {/* Donut chart — zeroed data renders the empty gray ring, so no-data
            cards keep the same silhouette as populated ones */}
        {status !== 'failed' && (
          <div className={styles.bodyRight}>
            <SentimentDonut
              data={
                sentiment ?? { negative: 0, neutral: 0, positive: 0, total: 0 }
              }
            />
          </div>
        )}
      </div>

      {/* ─── Footer: links + View Details ─── */}
      <div className={styles.footer}>
        {links.length > 0 && (
          <div className={styles.linksRow}>
            <LinkIcon className={styles.linkIcon} />
            <div className={styles.linksText}>
              {links.map((link, idx) => (
                <a
                  className={styles.link}
                  href={link}
                  key={idx}
                  rel="noopener noreferrer"
                  target="_blank"
                  title={link}
                >
                  {/* Readable label instead of the raw URL: the location name
                      (falls back to the source's hostname). Full URL on hover. */}
                  {title || hostLabel(link)}
                </a>
              ))}
            </div>
          </div>
        )}
        {!isPending && (
          <button
            className={styles.viewDetailsBtn}
            onClick={onViewDetails}
            type="button"
          >
            View Details <ArrowRightIcon className={styles.arrowIcon} />
          </button>
        )}
      </div>
    </div>
  )
}
